import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

/**
 * Zero-height anchor pinned to the bottom of the scrollport. It lives inside the scroll
 * container so the wheel scrolls the table natively when the pointer is over the hint,
 * and adds no scroll height of its own.
 */
const StickySlot = styled.div`
    position: sticky;
    bottom: 0;
    height: 0;
    z-index: 2;
`;

const Hint = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    gap: 8px;
    padding: 32px 0 8px;
    /* The hint sits over the last row, so it has to swallow clicks — otherwise they fall
       through and open that row's details blade. */
    cursor: default;
    font-family: Poppins;
    font-size: 14px;
    line-height: 22px;
    color: ${(props) => props.theme['Content/secondary']};
    /* the row underneath is still visible, so it is faded out rather than covered */
    background: linear-gradient(
        to bottom,
        transparent 0%,
        ${(props) => props.theme['Surface/page']} 65%
    );

    span.material-symbols-outlined {
        font-size: 18px;
    }
`;

// The hint only makes sense while there is content left below the fold, so it is
// hidden as soon as the user starts scrolling and once the bottom is reached.
const SCROLL_IDLE_DELAY = 500;
const BOTTOM_THRESHOLD = 8;

interface ScrollToSeeMoreProps {
    containerRef: React.RefObject<HTMLElement | null>;
    // Anything that changes the scrollable content, so the hint is re-evaluated.
    watch?: unknown;
}

const ScrollToSeeMore: React.FC<ScrollToSeeMoreProps> = ({ containerRef, watch }) => {
    const [hasMore, setHasMore] = useState(false);
    const [isScrolling, setIsScrolling] = useState(false);
    const idleTimer = useRef<ReturnType<typeof setTimeout>>();

    const updateHasMore = useCallback(() => {
        const container = containerRef.current;
        if (!container) {
            setHasMore(false);
            return;
        }
        const remaining = container.scrollHeight - container.scrollTop - container.clientHeight;
        setHasMore(remaining > BOTTOM_THRESHOLD);
    }, [containerRef]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const onScroll = () => {
            setIsScrolling(true);
            if (idleTimer.current) {
                clearTimeout(idleTimer.current);
            }
            idleTimer.current = setTimeout(() => {
                setIsScrolling(false);
                updateHasMore();
            }, SCROLL_IDLE_DELAY);
            updateHasMore();
        };

        const resizeObserver = new ResizeObserver(updateHasMore);
        resizeObserver.observe(container);

        container.addEventListener('scroll', onScroll);
        updateHasMore();

        return () => {
            container.removeEventListener('scroll', onScroll);
            resizeObserver.disconnect();
            if (idleTimer.current) {
                clearTimeout(idleTimer.current);
            }
        };
    }, [containerRef, updateHasMore]);

    useEffect(() => {
        updateHasMore();
    }, [watch, updateHasMore]);

    if (!hasMore || isScrolling) {
        return null;
    }

    return (
        <StickySlot>
            <Hint
                data-test-id="value-scroll-to-see-more"
                onClick={(event) => event.stopPropagation()}
            >
                <span className="material-symbols-outlined">arrow_downward</span>
                Scroll to see more
            </Hint>
        </StickySlot>
    );
};

export default ScrollToSeeMore;
